import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import {
  compareAsc,
  eachDayOfInterval,
  format,
  isPast,
  max,
  startOfToday,
} from "date-fns";

import query from "@/Utils/request/query";
import { dateQueryString, getMonthStartAndEnd } from "@/Utils/utils";
import {
  AvailabilityHeatmapDay,
  AvailabilityHeatmapResponse,
  PublicAppointment,
  SchedulableResourceType,
  TokenSlot,
} from "@/types/scheduling/schedule";
import scheduleApis from "@/types/scheduling/scheduleApi";

export const groupSlotsByAvailability = (slots: TokenSlot[]) => {
  const result: {
    availability: TokenSlot["availability"];
    slots: Omit<TokenSlot, "availability">[];
  }[] = [];

  for (const slot of slots) {
    // skip past slots
    if (isPast(slot.end_datetime)) {
      continue;
    }
    // skip fully allocated slots
    if (slot.allocated === slot.availability.tokens_per_slot) {
      continue;
    }
    const availability = slot.availability;
    const existing = result.find(
      (r) => r.availability.name === availability.name,
    );
    if (existing) {
      existing.slots.push(slot);
    } else {
      result.push({ availability, slots: [slot] });
    }
  }

  // sort slots by start time
  result.forEach(({ slots }) =>
    slots.sort((a, b) => compareAsc(a.start_datetime, b.start_datetime)),
  );

  // sort availability by first slot start time
  result.sort((a, b) =>
    compareAsc(a.slots[0].start_datetime, b.slots[0].start_datetime),
  );

  return result;
};

export const summarizeSlotAvailability = (
  slots: Pick<TokenSlot, "allocated" | "availability">[],
): AvailabilityHeatmapDay => {
  return {
    booked_slots: slots.reduce((total, slot) => total + slot.allocated, 0),
    total_slots: slots.reduce(
      (total, slot) => total + slot.availability.tokens_per_slot,
      0,
    ),
    total_time_slots: slots.length,
    available_time_slots: slots.filter(
      (slot) => slot.allocated < slot.availability.tokens_per_slot,
    ).length,
  };
};

/**
 * Get the availability heatmap for a user for a given month
 */
export const useAvailabilityHeatmap = ({
  facilityId,
  resourceId,
  month,
  resourceType,
}: {
  facilityId: string;
  resourceId?: string;
  month: Date;
  resourceType: SchedulableResourceType;
}) => {
  const { start, end } = getMonthStartAndEnd(month);

  // start from today if the month is current or past
  const fromDate = dateQueryString(max([start, startOfToday()]));

  // ensure toDate is not before fromDate
  const toDate = dateQueryString(max([fromDate, end]));

  let queryFn = query(scheduleApis.slots.availabilityStats, {
    pathParams: { facilityId },
    body: {
      // voluntarily coalesce to empty string since we know query would be
      // enabled only if userId is present
      resource_type: resourceType,
      resource_id: resourceId ?? "",
      from_date: fromDate,
      to_date: toDate,
    },
    silent: true,
  });

  if (careConfig.appointments.useAvailabilityStatsAPI === false) {
    queryFn = async () => getInfiniteAvailabilityHeatmap({ fromDate, toDate });
  }

  return useQuery({
    queryKey: ["availabilityHeatmap", resourceId, fromDate, toDate],
    queryFn,
    enabled: !!resourceId,
  });
};

const getInfiniteAvailabilityHeatmap = ({
  fromDate,
  toDate,
}: {
  fromDate: string;
  toDate: string;
}) => {
  const dates = eachDayOfInterval({ start: fromDate, end: toDate });

  const result: AvailabilityHeatmapResponse = {};

  for (const date of dates) {
    result[dateQueryString(date)] = {
      total_slots: Infinity,
      booked_slots: 0,
      total_time_slots: Infinity,
      available_time_slots: Infinity,
    };
  }

  return result;
};

export const formatAppointmentSlotTime = (appointment: PublicAppointment) => {
  if (!appointment.token_slot?.start_datetime) {
    return "";
  }
  return format(appointment.token_slot.start_datetime, "dd MMM, yyyy, hh:mm a");
};

export const formatSlotTimeRange = (slot: {
  start_datetime: string;
  end_datetime: string;
}) => {
  return `${format(slot.start_datetime, "h:mm a")} - ${format(
    slot.end_datetime,
    "h:mm a",
  )}`;
};
